import { compressData } from "@excalidraw/excalidraw/data/encode";
import { generateEncryptionKey } from "@excalidraw/excalidraw/data/encryption";
import { serializeAsJSON } from "@excalidraw/excalidraw/data/json";
import type { AppState, BinaryFiles } from "@excalidraw/excalidraw/types";
import type { ExcalidrawElement } from "@excalidraw/element/types";

const BACKEND_POST = import.meta.env.VITE_APP_BACKEND_V2_POST_URL;
const BACKEND_GET = import.meta.env.VITE_APP_BACKEND_V2_GET_URL;

export async function saveBoard(
  elements: readonly ExcalidrawElement[],
  appState: Partial<AppState>,
  files: BinaryFiles,
  opts: { id?: string; key?: string; name?: string; explicit?: boolean },
): Promise<{ id: string; key: string }> {
  const key = opts.key ?? (await generateEncryptionKey("string"));
  const payload = await compressData(
    new TextEncoder().encode(serializeAsJSON(elements, appState, files, "database")),
    { encryptionKey: key },
  );

  const headers: Record<string, string> = {};
  if (!opts.id) {
    headers["X-Encryption-Key"] = key;
  }
  if (opts.name) {
    headers["X-Board-Name"] = opts.name;
  }
  if (opts.explicit) {
    headers["X-Force-Checkpoint"] = "true";
  }

  const [url, method] = opts.id
    ? [`${BACKEND_GET}${opts.id}`, "PUT"]
    : [BACKEND_POST, "POST"];

  const res = await fetch(url, { method, body: payload.buffer, headers });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    if (json.error_class === "RequestTooLargeError") {
      throw new Error("Board is too large to save");
    }
    throw new Error(`Save failed: HTTP ${res.status}`);
  }
  const json = await res.json();
  const id = json.id ?? opts.id;
  if (!id) {
    throw new Error("Server did not return a board ID");
  }
  return { id, key };
}
